import { useState } from 'react'
import { ReminderManagement, FamilyDutyReport } from '@/features/admin'
import { useAuth } from '@/features/auth'

function UserMenu() {
  const { user, signOut } = useAuth()
  const [showMenu, setShowMenu] = useState(false)

  const handleSignOut = async () => {
    await signOut()
    setShowMenu(false)
  }

  return (
    <div className="relative">
      <button
        onClick={() => setShowMenu(!showMenu)}
        className="flex items-center text-sm bg-white/90 backdrop-blur-sm border border-gray-200 rounded-2xl px-4 py-3 hover:bg-white hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-200 shadow-sm"
      >
        <div className="w-8 h-8 bg-gradient-to-r from-primary to-accent rounded-full flex items-center justify-center ml-3">
          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        </div>
        <span className="font-hebrew text-gray-700 font-medium">
          {user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'משתמש'}
        </span>
        <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {showMenu && (
        <div className="absolute left-0 mt-2 w-56 bg-white/95 backdrop-blur-sm border border-gray-200 rounded-2xl shadow-xl z-10 overflow-hidden">
          <div className="py-2">
            <div className="px-4 py-3 text-sm text-gray-500 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-blue-50/30">
              <div className="font-hebrew font-medium text-gray-700">חשבון משתמש</div>
              <div className="text-xs text-gray-500 mt-1">{user?.email}</div>
            </div>
            <button
              onClick={handleSignOut}
              className="w-full text-right px-4 py-3 text-sm text-red-600 hover:bg-gradient-to-r hover:from-red-50 hover:to-pink-50 font-hebrew font-medium transition-all duration-200 flex items-center gap-2"
            >
              <svg className="w-4 h-4 mr-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              יציאה מהמערכת
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export function AdminReminderPage() {
  const { isAuthenticated, loading, isAdmin } = useAuth()
  const [adminTab, setAdminTab] = useState<'family_duties' | 'reminders'>('family_duties')

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center" dir="rtl">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 font-hebrew">טוען...</p>
        </div>
      </div>
    )
  }

  if (!isAuthenticated || !isAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center" dir="rtl">
        <div className="text-center bg-white rounded-lg shadow-lg p-8">
          <h1 className="text-2xl font-bold text-gray-900 font-hebrew mb-4">
            גישה נדחית
          </h1>
          <p className="text-gray-600 font-hebrew mb-6">
            רק מנהלים יכולים לגשת לעמוד זה
          </p>
          <a
            href="/"
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-hebrew font-semibold"
          >
            חזור לעמוד הראשי
          </a>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-indigo-50 dir-rtl" dir="rtl">
      <div className="container mx-auto px-4 py-8">
        {/* Navigation & Header */}
        <header className="mb-6">
          <div className="flex justify-between items-center mb-6">
            <a
              href="/"
              className="text-blue-600 hover:text-blue-700 font-hebrew font-semibold flex items-center gap-2 text-sm sm:text-base"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 12H5m7 7l-7-7 7-7" />
              </svg>
              חזור לעמוד הראשי
            </a>
            <UserMenu />
          </div>

          {/* Admin Navigation Tabs */}
          <div className="flex items-center gap-2 bg-white/80 p-1.5 rounded-2xl border border-gray-200 shadow-xs max-w-xl mx-auto">
            <button
              onClick={() => setAdminTab('family_duties')}
              className={`flex-1 py-2.5 px-4 rounded-xl text-xs sm:text-sm font-hebrew font-bold transition-all flex items-center justify-center gap-2 ${
                adminTab === 'family_duties'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100/70'
              }`}
            >
              <span>👨‍👩‍👧‍👦</span>
              <span>דוח משפחות ואחים</span>
            </button>

            <button
              onClick={() => setAdminTab('reminders')}
              className={`flex-1 py-2.5 px-4 rounded-xl text-xs sm:text-sm font-hebrew font-bold transition-all flex items-center justify-center gap-2 ${
                adminTab === 'reminders'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100/70'
              }`}
            >
              <span>⏰</span>
              <span>ניהול תזכורות</span>
            </button>
          </div>
        </header>

        {/* Tab Content */}
        <main className="max-w-6xl mx-auto">
          {adminTab === 'family_duties' ? (
            <FamilyDutyReport />
          ) : (
            <ReminderManagement />
          )}
        </main>
      </div>
    </div>
  )
}
